import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import IdsElement from '../../core/ids-element';

import styles from './ids-avatar.scss';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';

export enum AvatarTypes {
  Initials = 'initials',
  Image = 'image',
  Module = 'module'
}

export enum UserStatus {
  Available = 'available',
  Away = 'away',
  Busy = 'busy',
  DoNotDisturb = 'do-not-disturb',
  Unknown = 'unknown',
  Meeting = 'meeting'
}

const Base = IdsKeyboardMixin(
  IdsEventsMixin(
    IdsElement
  )
);

const DEFAULT_TYPE = AvatarTypes.Module;
const DEFAULT_COLOR = 'blue';

/**
 * IDS Dropdown Component
 * @type {IdsAvatar}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 * @part avatar - the tag element
 */
@customElement('ids-avatar')
@scss(styles)
export default class IdsAvatar extends Base {
  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();

    // Set defaults after connection to avoid Angular issues
    if (!this.getAttribute(attributes.TYPE)) {
      this.setAttribute(attributes.TYPE, DEFAULT_TYPE);
    }
    if (!this.getAttribute(attributes.COLOR)) {
      this.setAttribute(attributes.COLOR, DEFAULT_COLOR);
    }

    this.#attachEventHandlers();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.ALT,
      attributes.COLOR,
      attributes.TYPE,
      attributes.VALUE,
      attributes.USER_STATUS,
      attributes.SRC
    ];
  }

  /**
   * Sets up event listeners
   * @private
   * @returns {void}
   */
  #attachEventHandlers() {}

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const avatarTemplate = () => {
      switch (this.type) {
        case AvatarTypes.Initials: {
          const initialTemplate = `
            ${this.userStatus ? this.generateStatusIcon() : ''}
            <ids-text font-size="24"><slot></slot></ids-text>
          `;
          return initialTemplate;
        }
        case AvatarTypes.Image: {
          const imgTemplate = `
            ${this.userStatus ? this.generateStatusIcon() : ''}
            <img class="ids-avatar-image" src="${this.src ? this.src : ''}" alt="${this.alt ? this.alt : ''}" />
          `;
          return imgTemplate;
        }
        case AvatarTypes.Module:
          return `<ids-text font-size="14"><slot></slot></ids-text>`;
        default:
          return `
            ${this.userStatus ? this.generateStatusIcon() : ''}
            <ids-text font-size="24"><slot></slot></ids-text>
          `;
      }
    };

    const template = `<div class="ids-avatar" part="avatar" type="${this.type}" color="${this.color}">
      ${avatarTemplate()}
    </div>`;

    return template;
  }

  /**
   * Get the avatar type
   * @returns {string} the avatar type
   */
  get type() {
    return this.getAttribute(attributes.TYPE) || DEFAULT_TYPE;
  }

  /**
   * Set the type
   * @param {string | null} value The Type [null, toggle-btn]
   */
  set type(value: string | null) {
    if (value && Object.values(AvatarTypes).includes(value as AvatarTypes)) {
      this.setAttribute(attributes.TYPE, value);
    } else {
      this.removeAttribute(attributes.TYPE);
    }
  }

  /**
   * Get the avatar value
   * @returns {string | null} the avatar value
   */
  get value() { return this.getAttribute(attributes.VALUE); }

  /**
   * Set the value
   * @param {string | null} value The value
   */
  set value(value: string | null) {
    if (value) this.setAttribute(attributes.VALUE, value);
    else this.removeAttribute(attributes.VALUE);
  }

  /**
   * Get the user status
   * @returns {string | null} the user status
   */
  get userStatus() { return this.getAttribute(attributes.USER_STATUS); }

  /**
   * Set the user status
   * @param {string | null} value The user status value
   */
  set userStatus(value: string | null) {
    if (value && Object.values(UserStatus).includes(value as UserStatus)) {
      this.setAttribute(attributes.USER_STATUS, value);
    } else {
      this.removeAttribute(attributes.USER_STATUS);
    }
  }

  /**
   * Get the image source URL
   * @returns {string | null} the image source URL
   */
  get src() { return this.getAttribute(attributes.SRC); }

  /**
   * Set the src
   * @param {string | null} value The src value
   */
  set src(value: string | null) {
    if (value) this.setAttribute(attributes.SRC, value);
    else this.removeAttribute(attributes.SRC);
  }

  /**
   * Get the alt text for the image
   * @returns {string | null} the alt text
   */
  get alt() { return this.getAttribute(attributes.ALT); }

  /**
   * Set the alt
   * @param {string | null} value The alt value
   */
  set alt(value: string | null) {
    if (value) this.setAttribute(attributes.ALT, value);
    else this.removeAttribute(attributes.ALT);
  }

  /**
   * Get the color
   * @returns {string} the color
   */
  get color() {
    return this.getAttribute(attributes.COLOR) || DEFAULT_COLOR;
  }

  /**
   * Set the color
   * @param {string | null} value The color value
   */
  set color(value: string | null) {
    if (value) this.setAttribute(attributes.COLOR, value);
    else this.removeAttribute(attributes.COLOR);
  }

  /**
   * Generates an HTML string containing the status icon based on the user's status
   * @private
   * @returns {string} HTML string with the status icon or empty string if no valid status
   */
  generateStatusIcon() {
    if (this.userStatus && Object.values(UserStatus).includes(this.userStatus as UserStatus)) {
      let iconName = '';
      if (this.userStatus === 'meeting') iconName = 'circle-filled';
      else iconName = `user-status-${this.userStatus}`;

      return `<div class="icon-container" user-status="${this.userStatus}"><ids-icon icon="${iconName}" fill="inherit"></ids-icon></div>`;
    } return '';
  }
}
